import React from 'react';
import { TaskyBadge, TaskyCard } from '../tasky';
import {
  FACTOR_LABELS,
  preferenceStrength,
  type BannerAbFactor,
  type BannerAbImageSummary,
  type BannerAbTestComparison,
  type BannerAbVerdict,
} from '../../types/bannerAbTest';

const verdictLabel = (verdict: BannerAbVerdict | null) =>
  verdict === 'a' ? 'A案が優勢' : verdict === 'b' ? 'B案が優勢' : '判定保留';

const verdictVariant = (verdict: BannerAbVerdict | null) =>
  verdict === 'a' || verdict === 'b' ? ('info' as const) : ('secondary' as const);

const pct = (v: number | null | undefined) => (v == null ? '-' : `${Math.round(v * 100)}%`);
const num = (v: number | null | undefined, d = 1) => (v == null ? '-' : v.toFixed(d));

type Side = {
  key: Exclude<BannerAbVerdict, 'undecided'>;
  label: string;
  img: BannerAbImageSummary | null;
  mean: number | null;
};

/**
 * ペア比較1件のカード。集計済みなら勝敗・選ばれた割合・平均点・タイプ別内訳・決め手・理由、採点中なら進捗を出す。
 * 「B が勝つ確率」（p_b_better = Φ(δ/SE)）は出さない。票数 100 だと小さな差でも 0% / 100% に張り付き、
 * 同じ LLM の票は独立でないので確からしさを言い過ぎる（staging テスト #9 の指摘）。
 */
export const ComparisonCard: React.FC<{ comparison: BannerAbTestComparison }> = ({ comparison: c }) => {
  const aggregated = c.status === 'aggregated';
  const ci = c.delta != null && c.se != null ? `${num(c.delta, 2)} ± ${num(1.96 * c.se, 2)}` : '-';
  const sides: Side[] = [
    { key: 'a', label: 'A', img: c.image_a, mean: c.mean_a },
    { key: 'b', label: 'B', img: c.image_b, mean: c.mean_b },
  ];
  const factorRows = (Object.keys(FACTOR_LABELS) as BannerAbFactor[]).map((key) => ({
    key,
    label: FACTOR_LABELS[key],
    a: c.factors?.[key]?.a ?? 0,
    b: c.factors?.[key]?.b ?? 0,
  }));
  const factorMax = Math.max(1, ...factorRows.map((r) => r.a + r.b));
  const strength = preferenceStrength(c.verdict, c.win_rate_b);
  const shareB = c.win_rate_b;
  const segments = c.segments ?? [];

  return (
    <TaskyCard variant="bordered" padding="md" className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {sides.map((side) => (
          <div
            key={side.key}
            className={`rounded-md border p-2 ${c.verdict === side.key ? 'border-primary' : 'border-warm-border'}`}
          >
            <div className="text-xs font-semibold text-gray-500 mb-1">{side.label}案</div>
            {side.img ? (
              <img
                src={side.img.thumbnail_url || side.img.url}
                alt={`${side.label}案`}
                className="w-full h-40 object-contain bg-warm-surface rounded"
              />
            ) : (
              <div className="w-full h-40 bg-warm-surface rounded" />
            )}
            {aggregated && <div className="mt-2 text-sm text-dark">平均 {num(side.mean)} / 10</div>}
          </div>
        ))}
      </div>

      {!aggregated && c.status !== 'failed' && (
        <p className="text-sm text-gray-500">
          採点中… {c.votes_done + c.votes_skipped} / {c.persona_count} 票
        </p>
      )}
      {c.status === 'failed' && <p className="text-sm text-destructive">{c.error_message || '比較に失敗しました'}</p>}

      {aggregated && (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <TaskyBadge variant={verdictVariant(c.verdict)}>{verdictLabel(c.verdict)}</TaskyBadge>
            {strength && <span className="text-sm text-dark">{strength}</span>}
          </div>

          <div>
            <div className="flex items-baseline justify-between text-sm text-dark">
              <span>A を選んだ人 {shareB == null ? '-' : pct(1 - shareB)}</span>
              <span className="font-semibold">B を選んだ人 {pct(shareB)}</span>
            </div>
            {shareB != null && (
              <div
                className="mt-1 h-2 bg-gray-300 rounded overflow-hidden flex justify-end"
                role="img"
                aria-label={`B を選んだ人 ${pct(shareB)}`}
              >
                <div className="h-2 bg-primary" style={{ width: `${shareB * 100}%` }} />
              </div>
            )}
            <div className="mt-1 flex flex-wrap gap-3 text-xs text-gray-500">
              <span>平均点の差 {ci}（B − A、95%区間）</span>
              <span>
                有効 {c.votes_done} 票 / 無効 {c.votes_skipped} 票
              </span>
            </div>
          </div>

          {segments.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 mb-1">仮想顧客のタイプ別</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500">
                    <th className="text-left font-normal py-0.5">タイプ</th>
                    <th className="text-right font-normal py-0.5">人数</th>
                    <th className="text-right font-normal py-0.5">A 平均</th>
                    <th className="text-right font-normal py-0.5">B 平均</th>
                    <th className="text-right font-normal py-0.5">B を選んだ人</th>
                  </tr>
                </thead>
                <tbody>
                  {segments.map((s) => (
                    <tr key={s.key} className="border-t border-warm-border text-dark">
                      <td className="py-0.5 pr-2">{s.label}</td>
                      <td className="py-0.5 text-right">{s.n}</td>
                      <td className="py-0.5 text-right">{num(s.mean_a)}</td>
                      <td className="py-0.5 text-right">{num(s.mean_b)}</td>
                      <td className="py-0.5 text-right">{pct(s.preference_b)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-gray-500 mb-1">決め手（A 側 / B 側）</div>
            <div className="space-y-1">
              {factorRows.map((r) => (
                <div key={r.key} className="flex items-center gap-2 text-xs">
                  <span className="w-20 text-gray-600">{r.label}</span>
                  <div className="flex-1 h-2 bg-warm-surface rounded overflow-hidden flex">
                    <div className="h-2 bg-gray-300" style={{ width: `${(r.a / factorMax) * 100}%` }} />
                    <div className="h-2 bg-primary" style={{ width: `${(r.b / factorMax) * 100}%` }} />
                  </div>
                  <span className="w-16 text-right text-gray-500">
                    {r.a} / {r.b}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {c.reasons_top && c.reasons_top.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 mb-1">勝った側が選ばれた理由</div>
              <ul className="list-disc pl-5 text-sm text-dark space-y-0.5">
                {c.reasons_top.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </TaskyCard>
  );
};

export default ComparisonCard;
